import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowDown,
  ArrowUp,
  Check,
  GitMerge,
  ListOrdered,
  Pencil,
  Plus,
  Scissors,
  X,
} from 'lucide-react';
import { useEngagement } from '../hooks/useEngagement';
import { useSharedStream } from '../hooks/useSharedStream';
import {
  applySequenceOperation,
  deriveBusinessSequenceFromFlow,
  normalizeSequenceOverlay,
  resolveBusinessSequence,
  type BusinessSequenceStep,
  type SequenceOperationDraft,
  type StreamBusinessSequenceOverlay,
} from '../utils/streamBusinessSequenceModel';
import type {
  StreamWorkspaceReadPanelProps,
  StreamWorkspaceStream,
} from './streamMappingWorkspaceTypes';
import { EMPTY_STREAM_READ_SESSION } from './streamMappingWorkspaceTypes';
import '../styles/StreamBusinessSequencePanel.css';

function streamsFromSnapshot(snapshot: unknown): StreamWorkspaceStream[] {
  const streamsDoc = (snapshot as { streams?: { streams?: unknown } } | null)?.streams;
  return Array.isArray(streamsDoc?.streams)
    ? (streamsDoc.streams as StreamWorkspaceStream[])
    : [];
}

function evidenceLabel(count: number): string {
  if (count === 0) return 'Evidence missing';
  return `${count} piece${count === 1 ? '' : 's'} of evidence`;
}

const StreamBusinessSequencePanel: React.FC<StreamWorkspaceReadPanelProps> = ({
  stream,
  session = EMPTY_STREAM_READ_SESSION,
  readOnly = false,
}) => {
  const { snapshot, saveStreams, currentEngagementId } = useEngagement();
  const linked = Boolean(stream.sharedProcesses);
  const shared = useSharedStream(currentEngagementId, linked ? stream.id : null);
  const [overlay, setOverlay] = useState<StreamBusinessSequenceOverlay>(() =>
    normalizeSequenceOverlay(stream.sequenceOverlay),
  );
  const [expandedStepIds, setExpandedStepIds] = useState<string[]>([]);
  const [editingStepId, setEditingStepId] = useState<string | null>(null);
  const [editingLabel, setEditingLabel] = useState('');
  const [newStepLabel, setNewStepLabel] = useState('');
  const [splittingStepId, setSplittingStepId] = useState<string | null>(null);
  const [splitLabels, setSplitLabels] = useState<[string, string]>(['', '']);
  const [mergeStepId, setMergeStepId] = useState<string | null>(null);
  const [mergeLabel, setMergeLabel] = useState('');
  const [localSaving, setSaving] = useState(false);
  const saving = localSaving || shared.saving || readOnly;
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    setOverlay(normalizeSequenceOverlay(stream.sequenceOverlay));
  }, [stream.sequenceOverlay]);

  const flowText = session.newModel.flowText || '';
  const derivedSteps = useMemo(
    () => deriveBusinessSequenceFromFlow(flowText),
    [flowText],
  );
  const steps = useMemo(
    (): BusinessSequenceStep[] => linked ? (shared.shared?.steps || []).map((step, index) => ({
      id: step.stepId, label: step.label, order: index + 1, evidenceRefs: [], evidenceState: 'missing', sourceState: 'derived', sourceStepIds: [step.stepId],
    })) : resolveBusinessSequence({ flowText, overlay }),
    [flowText, overlay, linked, shared.shared],
  );

  const persistOverlay = useCallback(
    async (nextOverlay: StreamBusinessSequenceOverlay) => {
      if (linked || readOnly) return;
      setOverlay(nextOverlay);
      setSaving(true);
      setSaveError(null);
      try {
        const snapshotStreams = streamsFromSnapshot(snapshot);
        const streamList = snapshotStreams.length > 0 ? snapshotStreams : [stream];
        const hasStream = streamList.some((candidate) => candidate.id === stream.id);
        const nextStreams = (hasStream ? streamList : [...streamList, stream]).map((candidate) =>
          candidate.id === stream.id
            ? { ...candidate, sequenceOverlay: nextOverlay }
            : candidate,
        );
        await saveStreams({ streams: nextStreams });
      } catch (err) {
        setSaveError((err as Error).message || 'Could not save the sequence.');
      } finally {
        setSaving(false);
      }
    },
    [saveStreams, snapshot, stream, linked, readOnly],
  );

  const applyOperation = useCallback(
    (operation: SequenceOperationDraft) => {
      if (readOnly) return;
      if (linked) {
        const source = shared.shared?.steps.find(s => s.stepId === ('stepId' in operation ? operation.stepId : operation.afterStepId));
        const processId = source?.processId || shared.shared?.processIds.at(-1);
        if (!processId) { setSaveError('Select a linked process before adding a step.'); return; }
        let command: Record<string, unknown>;
        switch (operation.type) {
          case 'rename': command = { type: 'rename-step', stepId: operation.stepId, label: operation.label }; break;
          case 'split': command = { type: 'split-step', stepId: operation.stepId, labels: operation.labels }; break;
          case 'merge': {
            const target = shared.shared?.steps.find(s => s.stepId === operation.withStepId);
            if (target?.processId !== processId || target.parentStepId !== source?.parentStepId) { setSaveError('Merge steps within one process and parent. Move the step explicitly before merging across processes.'); return; }
            command = { type: 'merge-steps', stepIds: [operation.stepId, operation.withStepId], label: operation.label }; break;
          }
          case 'move': {
            const target = shared.shared?.steps[operation.toIndex ?? -1];
            if (!target || target.processId !== processId || target.parentStepId !== source?.parentStepId) { setSaveError('Use Event Storming to move a step between parents or processes.'); return; }
            const siblings = shared.shared?.steps.filter(s => s.processId === processId && s.parentStepId === source?.parentStepId) || [];
            const before = target.order < (source?.order ?? 0) ? target : siblings[siblings.findIndex(s => s.stepId === target.stepId) + 1];
            command = { type: 'reorder-step', stepId: operation.stepId, beforeStepId: before?.stepId || null }; break;
          }
          case 'add': command = { type: 'add-step', label: operation.label, parentStepId: source?.parentStepId || null }; break;
        }
        void shared.command(processId, command);
        return;
      }
      const nextOverlay = applySequenceOperation(overlay, operation, derivedSteps);
      void persistOverlay(nextOverlay);
    },
    [derivedSteps, overlay, persistOverlay, linked, shared, readOnly],
  );

  const toggleEvidence = (stepId: string) => {
    setExpandedStepIds((current) =>
      current.includes(stepId)
        ? current.filter((id) => id !== stepId)
        : [...current, stepId],
    );
  };

  const beginRename = (step: BusinessSequenceStep) => {
    setEditingStepId(step.id);
    setEditingLabel(step.label);
  };

  const saveRename = () => {
    if (!editingStepId || !editingLabel.trim()) return;
    applyOperation({
      origin: 'ui',
      type: 'rename',
      stepId: editingStepId,
      label: editingLabel,
    });
    setEditingStepId(null);
    setEditingLabel('');
  };

  const addStep = () => {
    if (!newStepLabel.trim()) return;
    const lastStep = steps[steps.length - 1];
    applyOperation({
      origin: 'ui',
      type: 'add',
      label: newStepLabel,
      ...(lastStep ? { afterStepId: lastStep.id } : {}),
    });
    setNewStepLabel('');
  };

  const beginSplit = (step: BusinessSequenceStep) => {
    setSplittingStepId(step.id);
    setSplitLabels([step.label, `${step.label} follow-up`]);
  };

  const saveSplit = () => {
    if (!splittingStepId || !splitLabels[0].trim() || !splitLabels[1].trim()) return;
    applyOperation({
      origin: 'ui',
      type: 'split',
      stepId: splittingStepId,
      labels: splitLabels,
    });
    setSplittingStepId(null);
    setSplitLabels(['', '']);
  };

  const beginMerge = (step: BusinessSequenceStep, nextStep: BusinessSequenceStep) => {
    setMergeStepId(step.id);
    setMergeLabel(`${step.label} and ${nextStep.label}`);
  };

  const saveMerge = () => {
    if (!mergeStepId || !mergeLabel.trim()) return;
    const stepIndex = steps.findIndex((step) => step.id === mergeStepId);
    const nextStep = stepIndex >= 0 ? steps[stepIndex + 1] : null;
    if (!nextStep) return;
    applyOperation({
      origin: 'ui',
      type: 'merge',
      stepId: mergeStepId,
      withStepId: nextStep.id,
      label: mergeLabel,
    });
    setMergeStepId(null);
    setMergeLabel('');
  };

  const moveStep = (step: BusinessSequenceStep, direction: 'up' | 'down') => {
    const index = steps.findIndex((candidate) => candidate.id === step.id);
    if (index < 0) return;
    applyOperation({
      origin: 'ui',
      type: 'move',
      stepId: step.id,
      toIndex: direction === 'up' ? index - 1 : index + 1,
    });
  };

  return (
    <section className="smw-leaf bsq" data-testid="stream-sequence-panel">
      <div className="smw-panel-head">
        <div>
          <h2>Business sequence</h2>
          <p>Review and adjust the ordered business steps for this stream.</p>
        </div>
        <ListOrdered size={18} aria-hidden="true" />
      </div>

      {saveError || shared.error ? (
        <p className="bsq-alert" role="alert">
          {saveError || shared.error}
        </p>
      ) : null}
      {linked ? <p>Shared processes: {shared.shared?.processes?.map(p => p.name).join(' · ') || 'Loading…'}. Changes update Event Storming and every linked stream.</p> : null}
      {shared.shared?.unresolvedReferences.map(ref => <p key={ref.stepId} role="alert">{ref.stepId}: {ref.reason}</p>)}

      {steps.length === 0 ? (
        <div className="bsq-empty">
          <p>No sequence captured yet.</p>
          <span>Keep narrating in chat and the first business steps will appear here.</span>
        </div>
      ) : (
        <>
          <ol className="bsq-list" aria-label="Business steps">
            {steps.map((step, index) => {
              const nextStep = steps[index + 1];
              const expanded = expandedStepIds.includes(step.id);
              const editing = editingStepId === step.id;
              const splitting = splittingStepId === step.id;
              const merging = mergeStepId === step.id && nextStep;

              return (
                <li
                  key={step.id}
                  className={`bsq-step bsq-step--${step.sourceState}`}
                  data-testid="business-sequence-step"
                  data-step-label={step.label}
                >
                  <div className="bsq-step-order" aria-hidden="true">
                    {step.order}
                  </div>

                  <div className="bsq-step-body">
                    <div className="bsq-step-main">
                      {editing ? (
                        <label className="bsq-inline-field">
                          <span>Step label for {step.label}</span>
                          <input
                            value={editingLabel}
                            onChange={(event) => setEditingLabel(event.currentTarget.value)}
                          />
                        </label>
                      ) : (
                        <h3>{step.label}</h3>
                      )}
                      <div className="bsq-meta">
                        <span>{evidenceLabel(step.evidenceRefs.length)}</span>
                        {step.sourceState !== 'derived' ? <span>{step.sourceState}</span> : null}
                      </div>
                    </div>

                    <div className="bsq-actions" aria-label={`Actions for ${step.label}`}>
                      {editing ? (
                        <>
                          <button type="button" onClick={saveRename} aria-label="Save name">
                            <Check size={15} aria-hidden="true" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditingStepId(null)}
                            aria-label="Cancel rename"
                          >
                            <X size={15} aria-hidden="true" />
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => moveStep(step, 'up')}
                            disabled={index === 0 || saving}
                            aria-label={`Move ${step.label} up`}
                          >
                            <ArrowUp size={15} aria-hidden="true" />
                          </button>
                          <button
                            type="button"
                            onClick={() => moveStep(step, 'down')}
                            disabled={index === steps.length - 1 || saving}
                            aria-label={`Move ${step.label} down`}
                          >
                            <ArrowDown size={15} aria-hidden="true" />
                          </button>
                          <button
                            type="button"
                            onClick={() => beginRename(step)}
                            disabled={saving}
                            aria-label={`Rename ${step.label}`}
                          >
                            <Pencil size={15} aria-hidden="true" />
                          </button>
                          <button
                            type="button"
                            onClick={() => beginSplit(step)}
                            disabled={saving}
                            aria-label={`Split ${step.label}`}
                          >
                            <Scissors size={15} aria-hidden="true" />
                          </button>
                          <button
                            type="button"
                            onClick={() => nextStep && beginMerge(step, nextStep)}
                            disabled={!nextStep || saving}
                            aria-label={
                              nextStep
                                ? `Merge ${step.label} with ${nextStep.label}`
                                : `Merge ${step.label}`
                            }
                          >
                            <GitMerge size={15} aria-hidden="true" />
                          </button>
                        </>
                      )}
                    </div>

                    {splitting ? (
                      <div className="bsq-edit-row">
                        <label>
                          <span>First split label for {step.label}</span>
                          <input
                            value={splitLabels[0]}
                            onChange={(event) =>
                              setSplitLabels([event.currentTarget.value, splitLabels[1]])
                            }
                          />
                        </label>
                        <label>
                          <span>Second split label for {step.label}</span>
                          <input
                            value={splitLabels[1]}
                            onChange={(event) =>
                              setSplitLabels([splitLabels[0], event.currentTarget.value])
                            }
                          />
                        </label>
                        <button type="button" onClick={saveSplit} disabled={saving}>
                          <Check size={15} aria-hidden="true" />
                          Save split
                        </button>
                        <button
                          type="button"
                          onClick={() => setSplittingStepId(null)}
                          disabled={saving}
                        >
                          <X size={15} aria-hidden="true" />
                          Cancel
                        </button>
                      </div>
                    ) : null}

                    {merging ? (
                      <div className="bsq-edit-row">
                        <label>
                          <span>Merged step label for {step.label} and {nextStep.label}</span>
                          <input
                            value={mergeLabel}
                            onChange={(event) => setMergeLabel(event.currentTarget.value)}
                          />
                        </label>
                        <button type="button" onClick={saveMerge} disabled={saving}>
                          <GitMerge size={15} aria-hidden="true" />
                          Confirm merge
                        </button>
                        <button
                          type="button"
                          onClick={() => setMergeStepId(null)}
                          disabled={saving}
                        >
                          <X size={15} aria-hidden="true" />
                          Cancel
                        </button>
                      </div>
                    ) : null}

                    <button
                      type="button"
                      className="bsq-evidence-toggle"
                      onClick={() => toggleEvidence(step.id)}
                      aria-expanded={expanded}
                    >
                      {expanded ? 'Hide' : 'Show'} evidence for {step.label}
                    </button>

                    {expanded ? (
                      step.evidenceRefs.length > 0 ? (
                        <ul className="bsq-evidence-list">
                          {step.evidenceRefs.map((ref) => (
                            <li key={ref.id}>{ref.summary}</li>
                          ))}
                        </ul>
                      ) : (
                        <p className="bsq-missing">No captured evidence is attached yet.</p>
                      )
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ol>

          <div className="bsq-add">
            <label>
              <span>New step label</span>
              <input
                value={newStepLabel}
                onChange={(event) => setNewStepLabel(event.currentTarget.value)}
              />
            </label>
            <button type="button" onClick={addStep} disabled={saving || !newStepLabel.trim()}>
              <Plus size={15} aria-hidden="true" />
              Add step
            </button>
          </div>
        </>
      )}

      {saving ? <p className="bsq-saving">Saving sequence...</p> : null}
    </section>
  );
};

export default StreamBusinessSequencePanel;
